import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, ArrowLeft } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { AutoTranslate } from "@/lib/autoTranslate.tsx";
import { PaystackPayment } from "@/components/PaystackPayment";
import { useToast } from "@/hooks/use-toast";
import {
  buildScheduledDowngradeMessage,
  formatSubscriptionEndDate,
  hasActiveProAccess,
} from "@/lib/subscription";
import { SEO } from "@/components/SEO";

export default function Pricing() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const annualPricingTrans = AutoTranslate(
    `Save 25% – billed annually ($90/year)`,
  );
  const signInTrans = AutoTranslate(`Sign In`);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "monthly",
  );
  const { user, loading: authLoading } = useAuth();
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [subscriptionCanceled, setSubscriptionCanceled] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isDowngrading, setIsDowngrading] = useState(false);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (authLoading) {
        return;
      }

      setIsLoadingProfile(true);

      try {
        if (!user) {
          setSubscriptionTier(null);
          return;
        }

        const { data } = await supabase
          .from("profiles")
          .select("subscription_tier, subscription_end, subscription_canceled")
          .eq("user_id", user.id)
          .single();

        if (data) {
          setSubscriptionTier(data.subscription_tier);
          setSubscriptionEnd(data.subscription_end);
          setSubscriptionCanceled(Boolean(data.subscription_canceled));
        }
      } catch (error) {
        console.error("Error fetching subscription tier:", error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchSubscription();
  }, [authLoading, user]);

  const handleGetStarted = () => {
    if (user) {
      window.location.href = "/";
    } else {
      window.location.href = "/auth";
    }
  };

  // Stripe checkout logic kept for reference while Paystack is active.
  // const handleUpgrade = async (priceId: string) => {
  //   try {
  //     const { data, error } = await supabase.functions.invoke(
  //       "create-checkout",
  //       {
  //         body: { priceId },
  //       },
  //     );
  //     if (error) throw error;
  //     if (data?.url) {
  //       window.location.href = data.url;
  //     }
  //   } catch (error) {
  //     console.error("Error creating checkout session:", error);
  //   }
  // };

  type FeatureItem = string | { text: string; bold: string; suffix: string };

  const plans: {
    starter: {
      name: string;
      price: { monthly: number; yearly: number };
      description: string;
      features: FeatureItem[];
    };
    pro: {
      name: string;
      price: { monthly: number; yearly: number };
      // Stripe price IDs kept disabled while Paystack is active.
      // priceIds: { monthly: string; yearly: string };
      description: string;
      features: FeatureItem[];
    };
  } = {
    starter: {
      name: AutoTranslate(`Starter`),
      price: { monthly: 0, yearly: 0 },
      description: AutoTranslate(`Begin your" journey with essential tools`),
      features: [
        AutoTranslate(`Smart AI-powered conversational assistance`),
        AutoTranslate(`3 'Refresh Chat' each month (3 resumes)`),
        AutoTranslate(`3-min audio recording time`),
      ],
    },
    pro: {
      name: AutoTranslate(`Pro`),
      price: { monthly: 10, yearly: 90 },
      // Stripe price IDs kept for reference only. Paystack now handles checkout.
      // priceIds: {
      //   monthly: "price_1RtEWJRv2gnIYYmyNdowZYc9",
      //   yearly: "price_1RtEgURv2gnIYYmyZGKfVWqK",
      // },
      description: AutoTranslate(`Everything you need to land your dream job`),
      features: [
        AutoTranslate(`Smart AI-powered conversational assistance`),
        AutoTranslate(`Unlimited 'Refresh Chat' = unlimited resumes`),
        AutoTranslate(`Access to Saved Resumes page`),
        AutoTranslate(`Unlimited audio recording time`),
        AutoTranslate(`Optimize your old resume`),
        {
          text: AutoTranslate(`Share your resume with other `),
          bold: AutoTranslate(`app users`),
          suffix: AutoTranslate(` and get feedback`),
        },
        AutoTranslate(`Track your shared resume analytics`),
      ],
    },
  };

  const getProPrice = () => {
    return billingCycle === "monthly"
      ? plans.pro.price.monthly
      : plans.pro.price.yearly;
  };

  const getPlanName = () => {
    return billingCycle === "monthly" ? "Pro Monthly" : "Pro Yearly";
  };

  const handlePaymentSuccess = async () => {
    if (user) {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_tier, subscription_end, subscription_canceled")
        .eq("user_id", user.id)
        .single();

      if (data) {
        setSubscriptionTier(data.subscription_tier);
        setSubscriptionEnd(data.subscription_end);
        setSubscriptionCanceled(Boolean(data.subscription_canceled));
      }
    }
  };

  const handleDowngrade = async () => {
    if (!user || isDowngrading || subscriptionCanceled) return;

    try {
      setIsDowngrading(true);
      const { data, error } = await supabase.functions.invoke("cancel-subscription");
      if (error) throw error;

      const endDate = data?.periodEnd || data?.subscription_end || subscriptionEnd;
      await handlePaymentSuccess();
      toast({
        title: "Downgrade scheduled",
        description: buildScheduledDowngradeMessage(endDate),
        duration: 20000,
      });
    } catch (error) {
      toast({
        title: "Could not downgrade",
        description: "Please try again. If this keeps happening, contact support.",
        variant: "destructive",
      });
    } finally {
      setIsDowngrading(false);
    }
  };

  const isPro = hasActiveProAccess({
    subscription_tier: subscriptionTier,
    subscription_end: subscriptionEnd,
    subscription_canceled: subscriptionCanceled,
  });

  interface Testimonial {
    name: string;
    role: string;
    image: string;
    quote: string;
  }

  const testimonials: Testimonial[] = [
    {
      name: "Sarah Johnson",
      role: AutoTranslate(`Marketing Manager`),
      image: "/lovable-uploads/0dbc4b90-314f-455f-8cbe-18da8a38afcd.png",
      quote:
        "Before using FIND, I barely got follow-ups from recruiters - just ghosting. But after building my new resume, recruiters were so impressed that they had to know more about me. I went from being ignored to getting actual interview calls within weeks!",
    },
    {
      name: AutoTranslate(`Marcus Williams`),
      role: "Senior Developer",
      image: "/lovable-uploads/77ba5b9b-cec3-4efa-8e73-f1d53b543eb4.png",
      quote:
        "After job hunting for 2 months straight with this resume, I finally got my first interview. Thank FIND for this awesome resume builder - normally it took me 6-8 months to even get anywhere with my old resume.",
    },
    {
      name: "Maya Chen",
      role: AutoTranslate(`UX Designer`),
      image: "/lovable-uploads/d61020dd-d633-4cf3-b33b-009fcf65b52b.png",
      quote:
        "My old resume was so old-fashioned - never knew that till a recruiter rejected my application with feedback about my resume format. This tool built me a new one and now I got my first callback for a remote job. Now I can travel the world and work from anywhere!",
    },
    {
      name: "Emma Rodriguez",
      role: AutoTranslate(`Project Manager`),
      image: "/lovable-uploads/6758b1a9-81e1-4662-b753-0b183e5e9632.png",
      quote:
        "The resumes have been so impressive that they've taken me a step further to the interview stage consistently. Before, I was lucky to even get a rejection message - most companies just ghosted me completely.",
    },
    {
      name: "Alex Thompson",
      role: AutoTranslate(`Data Analyst`),
      image: "/lovable-uploads/0c41b5f9-c609-4d57-9524-46bba33e81a3.png",
      quote:
        "I saw significant improvements immediately. The recruiter was so impressed with my resume that they scheduled a call within 24 hours of submitting my application. That had never happened before in my 5 years of job searching.",
    },
    {
      name: "Jordan Kim",
      role: AutoTranslate(`Software Engineer`),
      image: "/lovable-uploads/b40a0d98-a34c-4398-9d16-8212e85ce1c3.png",
      quote:
        "From getting zero responses to landing three interviews in one week. The difference was night and day. FIND's resume builder helped me showcase my skills in a way that actually caught recruiters' attention.",
    },
    {
      name: "Chris Anderson",
      role: AutoTranslate(`Marketing Specialist`),
      image: "/lovable-uploads/75d2e224-0391-4567-8b8b-83b8d0de59cf.png",
      quote:
        "Used to wait months for any response, now I'm getting callbacks within days. The professional formatting and ATS optimization really work - my resume is finally getting past the initial screening stage.",
    },
    {
      name: "Priya Patel",
      role: AutoTranslate(`Business Analyst`),
      image: "/lovable-uploads/812d71b7-6598-40f2-84dc-98d846681a89.png",
      quote:
        "The transformation was incredible. I went from being completely overlooked to having recruiters reach out to me directly. My new resume opened doors I never thought possible in my industry.",
    },
    {
      name: "Rachel Martinez",
      role: AutoTranslate(`HR Manager`),
      image: "/lovable-uploads/3c454126-1428-4af3-aebe-930bdfcb8cbd.png",
      quote:
        "As someone who reviews resumes daily, I can tell you this tool creates documents that stand out. After using it myself, I landed my dream role within 3 weeks of starting my job search.",
    },
    {
      name: "Michael Davis",
      role: "Sales Manager",
      image: "/lovable-uploads/c7412937-76f9-4e7f-a401-1963f7d514ff.png",
      quote:
        "The difference was like night and day. Before FIND, I was sending hundreds of applications with barely any responses. Now recruiters are calling me within hours of applying. This tool literally changed my career trajectory.",
    },
  ];

  return (
    <main className="min-h-screen bg-gradient-main">
      <SEO
        title="Pricing — FIND Resume Builder Free & Pro Plans"
        description="Compare FIND Resume Builder plans. Start free or upgrade to Pro for unlimited AI resumes, job tailoring, and premium templates. Monthly and annual pricing."
        path="/pricing"
      />
      {/* Background Pattern */}
      <div
        className="fixed inset-0 opacity-30 pointer-events-none dark:opacity-10"
        style={{
          backgroundImage: `var(--grid-pattern)`,
        }}
      />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between p-6 border-b border-border bg-card/80 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {AutoTranslate(`Back`)}
          </Button>
          <div className="text-2xl font-bold text-primary">
            {t("Pricing")}
          </div>
        </div>
        <div className="flex items-center gap-4">
          {!user && (
            <Button
              variant="ghost"
              onClick={() => (window.location.href = "/auth")}
            >
              {signInTrans}
            </Button>
          )}
          <Button onClick={handleGetStarted}>
            {t("landing.getStarted")}
          </Button>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="relative z-10 py-20 px-0">
        {" "}
        {/* Removed the gap on the sides */}
        <div className="mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {AutoTranslate(
                `Get started for FREE. Upgrade to get additional benefits.`,
              )}
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              {AutoTranslate(`Choose the plan that works best for you`)}
            </p>

            {/* Billing Toggle */}
            <Tabs
              value={billingCycle}
              onValueChange={(value) =>
                setBillingCycle(value as "monthly" | "yearly")
              }
              className="inline-block"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="monthly">
                  {AutoTranslate(`Monthly`)}
                </TabsTrigger>
                <TabsTrigger value="yearly">
                  {AutoTranslate(`Yearly`)}
                  <Badge variant="secondary" className="ml-2">
                    Save 25%
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {" "}
            {/* Increased max width for the content*/}
            {/* Starter Plan */}
            <Card className={`relative bg-card ${isPro ? "border-card-border" : "border-primary shadow-lg"}`}>
              {!isPro && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground">
                    {AutoTranslate(`Current Plan`)}
                  </Badge>
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-2xl">{plans.starter.name}</CardTitle>
                <CardDescription>{plans.starter.description}</CardDescription>
                <div className="flex items-baseline gap-2">
                  {" "}
                  {/* adjusted weight and size */}
                  <span className="text-5xl font-extrabold text-foreground">
                    {AutoTranslate(`FREE`)}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {isPro ? (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={handleDowngrade}
                    disabled={isDowngrading || subscriptionCanceled}
                  >
                    {subscriptionCanceled
                      ? `Downgrade scheduled for ${formatSubscriptionEndDate(subscriptionEnd)}`
                      : isDowngrading
                        ? "Scheduling downgrade..."
                        : "Downgrade"}
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    variant="outline"
                    disabled
                  >
                    {AutoTranslate(`Current Plan`)}
                  </Button>
                )}
                <ul className="divide-y divide-gray-200">
                  {/* divider added */}
                  {plans.starter.features.map((feature, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                    >
                      <Check className="h-5 w-5 text-green-500" />
                      <span className="text-muted-foreground">
                        {typeof feature === "string" ? (
                          feature
                        ) : (
                          <>
                            {feature.text}
                            <strong>{feature.bold}</strong>
                            {feature.suffix}
                          </>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            {/* Pro Plan */}
            <Card className={`relative bg-card ${isPro ? "border-primary shadow-lg scale-105" : "border-card-border"}`}>
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-primary text-primary-foreground">
                  {isPro
                    ? AutoTranslate(`Current Plan`)
                    : AutoTranslate(`Most Popular`)}
                </Badge>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl">{plans.pro.name}</CardTitle>
                <CardDescription>{plans.pro.description}</CardDescription>
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-extrabold text-foreground">
                      ${billingCycle === "yearly" ? "7.50" : "10"}
                    </span>
                    <span className="text-lg text-muted-foreground font-medium">
                      /month
                    </span>
                  </div>
                  {billingCycle === "yearly" && (
                    <p className="text-sm text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-md">
                      {annualPricingTrans}
                    </p>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {isLoadingProfile ? (
                  <Button className="w-full" variant="outline" disabled>
                    Checking plan...
                  </Button>
                ) : isPro ? (
                  <Button className="w-full" variant="outline" disabled>
                    {AutoTranslate(`Current Plan - Active`)}
                  </Button>
                ) : user ? (
                  <PaystackPayment
                    email={user.email || ""}
                    amount={getProPrice()}
                    planName={getPlanName()}
                    onSuccess={handlePaymentSuccess}
                    buttonText={AutoTranslate(`Upgrade to Pro`)}
                  />
                ) : (
                  <Button
                    className="w-full"
                    onClick={() => (window.location.href = "/auth")}
                  >
                    {AutoTranslate(`Upgrade to Pro`)}
                  </Button>
                )}
                <ul className="divide-y divide-gray-200">
                  {" "}
                  {/* divider added */}
                  {plans.pro.features.map((feature, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                    >
                      <Check className="h-5 w-5 text-green-500" />
                      <span className="text-muted-foreground">
                        {typeof feature === "string" ? (
                          feature
                        ) : (
                          <>
                            {feature.text}
                            <strong>{feature.bold}</strong>
                            {feature.suffix}
                          </>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Testimonials Marquee */}
          <section className="mt-16 mr-0">
            <h3 className="text-2xl font-bold text-center text-foreground mb-8">
              {AutoTranslate(
                `Hear what others who bought Pro plan have to say`,
              )}
            </h3>

            <div className="overflow-hidden w-full">
              <div
                className="flex animate-marquee-faster space-x-2"
                onMouseEnter={(e) =>
                  (e.currentTarget.style.animationPlayState = "paused")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.animationPlayState = "running")
                }
              >
                {/* First set */}
                {testimonials.map((item, index) => (
                  <div
                    key={`set1-${index}`}
                    className="flex-shrink-0 w-80 bg-card border border-card-border rounded-lg p-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <h4 className="font-semibold text-foreground">
                          {item.name}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {item.role}
                        </p>
                      </div>
                    </div>
                    <p className="text-muted-foreground text-justify">
                      "{item.quote}"
                    </p>
                  </div>
                ))}
                {/* Second set for seamless loop */}
                {testimonials.map((item, index) => (
                  <div
                    key={`set2-${index}`}
                    className="flex-shrink-0 w-80 bg-card border border-card-border rounded-lg p-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <h4 className="font-semibold text-foreground">
                          {item.name}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {item.role}
                        </p>
                      </div>
                    </div>
                    <p className="text-muted-foreground text-justify">
                      "{item.quote}"
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}